import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import {
  getOneOfType,
  type IxcFundingV1BatchTransferData,
  type IxcFundingV1BatchTransferDataCounts,
  type IxcFundingV1BatchTransferSpec,
  type IxcFundingV1BatchTransferState,
  type IxcFundingV1BatchTransferType,
  type IxcFundingV1FundingAllocation,
  type IxcFundingV1FundingBatchTransaction,
} from '@interxfi/types';
import { abs } from '@interxfi/utilities/number/abs';
import { getNumberOrUndefinedFromNullable, getNumericStringOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getRegistrationAccountDetailPath } from '../../registration/account/registration-account-routing';
import { getProgramDetailPath } from '../../userauth/program/program-routing';
import { getFundingSourceDetailPath } from '../source/funding-source-routing';
import { getFundingTransferAchPullListItems, getFundingTransferAchPushListItems } from '../transfer/funding-transfer-list-items';
import { getFundingTransferDetailPath } from '../transfer/funding-transfer-routing';
import { FundingBatchTransferDirectionPill } from './funding-batch-transfer-direction-pill';
import { getFundingBatchTransferDetailPath } from './funding-batch-transfer-routing';
import { FundingBatchTransferStatusPill } from './funding-batch-transfer-status-pill';

export function getFundingAllocationListItems(data: IxcFundingV1FundingAllocation | undefined, prefix = '') {
  return {
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingBatchTransferTypeListItems(data: IxcFundingV1BatchTransferType | undefined, prefix = '') {
  const typeNameItem = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcFundingV1',
      oneOfName: 'IxcFundingV1BatchTransferType',
      value: getOneOfType(data),
    }),
    typeNamePill: getReadonlyListItemPill(`${prefix}typeNamePill`, 'Type', data ? <FundingBatchTransferDirectionPill type={data} /> : null),
    typeNamePillWithoutPrefix: getReadonlyListItemPill(
      `${prefix}typeNamePillWithoutPrefix`,
      'Type',
      data ? <FundingBatchTransferDirectionPill type={data} withoutTypePrefix /> : null,
    ),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameItem;
  }

  if ('achPush' in data) {
    return {
      ...typeNameItem,
      achPush: {
        '!type': 'achPush',
        value: getFundingTransferAchPushListItems(data.achPush),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('achPull' in data) {
    return {
      ...typeNameItem,
      achPull: {
        '!type': 'achPull',
        value: getFundingTransferAchPullListItems(data.achPull),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameItem;
}

export function getFundingBatchTransferSpecListItems(data: IxcFundingV1BatchTransferSpec | undefined, prefix = '') {
  const totalAmount = getNumericStringOrUndefinedFromNullable(data, 'totalAmount');

  return {
    totalAmount: getReadonlyListItemCurrency(`${prefix}totalAmount`, 'Total Amount', totalAmount),
    totalAmountAbs: getReadonlyListItemCurrency(`${prefix}totalAmountAbs`, 'Total Amount', totalAmount ? abs(totalAmount) : undefined),
    allocations: data?.allocations?.map((allocation, index) => getFundingAllocationListItems(allocation, `${prefix}allocations.${index}.`)),
    allocationCount: getReadonlyListItemNumber(
      `${prefix}allocationCount`,
      'Allocation Count',
      data?.allocations ? data.allocations.length : undefined,
    ),
    batchTransferType: getFundingBatchTransferTypeListItems(data?.batchTransferType, `${prefix}batchTransferType.`),
    sourceId: data?.sourceId
      ? getReadonlyListItemId(`${prefix}sourceId`, 'Source ID', data.sourceId, getFundingSourceDetailPath(data.sourceId))
      : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingBatchTransferCountsListItems(data: IxcFundingV1BatchTransferDataCounts | undefined, prefix = '') {
  return {
    totalInternal: getReadonlyListItemNumber(`${prefix}totalInternal`, 'Total Internal', getNumberOrUndefinedFromNullable(data, 'totalInternal')),
    totalExternal: getReadonlyListItemNumber(`${prefix}totalExternal`, 'Total External', getNumberOrUndefinedFromNullable(data, 'totalExternal')),
    createdInternal: getReadonlyListItemNumber(
      `${prefix}createdInternal`,
      'Created Internal',
      getNumberOrUndefinedFromNullable(data, 'createdInternal'),
    ),
    createdExternal: getReadonlyListItemNumber(
      `${prefix}createdExternal`,
      'Created External',
      getNumberOrUndefinedFromNullable(data, 'createdExternal'),
    ),
    successInternal: getReadonlyListItemNumber(
      `${prefix}successInternal`,
      'Success Internal',
      getNumberOrUndefinedFromNullable(data, 'successInternal'),
    ),
    successExternal: getReadonlyListItemNumber(
      `${prefix}successExternal`,
      'Success External',
      getNumberOrUndefinedFromNullable(data, 'successExternal'),
    ),
    errorInternal: getReadonlyListItemNumber(`${prefix}errorInternal`, 'Error Internal', getNumberOrUndefinedFromNullable(data, 'errorInternal')),
    errorExternal: getReadonlyListItemNumber(`${prefix}errorExternal`, 'Error External', getNumberOrUndefinedFromNullable(data, 'errorExternal')),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingBatchTransactionListItems(data: IxcFundingV1FundingBatchTransaction | undefined, prefix = '') {
  return {
    batchId: getReadonlyListItemId(`${prefix}batchId`, 'Batch ID', data?.batchId, getFundingBatchTransferDetailPath(data?.batchId)),
    batchAllocationId: getReadonlyListItemId(`${prefix}batchAllocationId`, 'Batch Allocation ID', data?.batchAllocationId),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    programId: getReadonlyListItemId(`${prefix}programId`, 'Program ID', data?.programId, getProgramDetailPath(data?.programId)),
    timestamp: getReadonlyListItemDate(`${prefix}timestamp`, 'Timestamp', data?.timestamp, 'date_iso'),
    fundingAllocation: getFundingAllocationListItems(data?.fundingAllocation, `${prefix}fundingAllocation.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingBatchTransferDataListItems(data: IxcFundingV1BatchTransferData | undefined, prefix = '') {
  return {
    spec: getFundingBatchTransferSpecListItems(data?.spec, `${prefix}spec.`),
    externalId: data?.externalId ? getReadonlyListItemId(`${prefix}externalId`, 'External ID', data.externalId) : undefined,
    platformAccountId: getReadonlyListItemId(
      `${prefix}platformAccountId`,
      'Platform Account ID',
      data?.platformAccountId,
      getRegistrationAccountDetailPath(data?.platformAccountId),
    ),
    transferId: data?.transferId
      ? getReadonlyListItemId(`${prefix}transferId`, 'Transfer ID', data.transferId, getFundingTransferDetailPath(data.transferId))
      : undefined,
    counts: data?.counts ? getFundingBatchTransferCountsListItems(data.counts, `${prefix}counts.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingBatchTransferStateListItems(data: IxcFundingV1BatchTransferState | undefined) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    batchTransferId: getReadonlyListItemId('batchTransferId', 'Batch Transfer ID', data?.batchTransferId),
    programId: getReadonlyListItemId('programId', 'Program ID', data?.programId, getProgramDetailPath(data?.programId)),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcFundingV1',
      enumName: 'IxcFundingV1BatchTransferStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <FundingBatchTransferStatusPill status={data.status} /> : null),
    data: getFundingBatchTransferDataListItems(data?.data, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}
